import { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import { ReactFlow, Background, BackgroundVariant, Panel, useReactFlow } from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import { useTheme } from '@Sage/hoi-poi-ui';

import AutomationBuilderTriggerBox from './types/AutomationBuilderTriggerBox';
import AutomationBuilderConditionsBox from './types/AutomationBuilderConditionsBox';
import AutomationBuilderActionBox from './types/AutomationBuilderActionBox';
import AutomationBuilderCanvasPanel from './AutomationBuilderCanvasPanel';
import AutomationButtonEdge from './components/AutomationButtonEdge';

const DEFAULT_NODE_WIDTH = 450;
const DEFAULT_NODE_HEIGHT = 139;

const nodeTypes = {
    trigger: AutomationBuilderTriggerBox,
    conditions: AutomationBuilderConditionsBox,
    action: AutomationBuilderActionBox,
};

const edgeTypes = {
    buttonedge: AutomationButtonEdge,
};

const getLayoutedElements = (nodes, edges, dimensions) => {
    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
        rankdir: 'TB',
        ranksep: 50,
    });

    nodes.forEach((node) => {
        const width = dimensions[node.id]?.width || DEFAULT_NODE_WIDTH;
        const height = dimensions[node.id]?.height || DEFAULT_NODE_HEIGHT;
        dagreGraph.setNode(node.id, { width, height });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const newNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const width = dimensions[node.id]?.width || DEFAULT_NODE_WIDTH;
        const height = dimensions[node.id]?.height || DEFAULT_NODE_HEIGHT;

        const newNode = {
            ...node,
            targetPosition: 'top',
            sourcePosition: 'bottom',
            style: { opacity: 1 },
            position: {
                x: nodeWithPosition.x - width / 2,
                y: nodeWithPosition.y - height / 2,
            },
        };

        return newNode;
    });

    return newNodes;
};

const AutomationBuilderCanvasFlow = memo(({ data, onChange, nodes, edges, setEdges, setNodes }) => {
    const reactFlow = useReactFlow();
    const theme = useTheme();
    const proOptions = useMemo(() => ({ hideAttribution: true }), []);
    const defaultEdgeOptions = useMemo(() => ({ className: 'fm-automation-builder__edge' }), []);
    const dimensions = useRef({});

    const onNodesChange = useCallback(
        (changes) => {
            const dimensionsChanges = changes.filter((change) => change?.dimensions);
            const isAReplace = changes.some((change) => change?.type === 'replace');
            if (dimensionsChanges.length === 0 && !isAReplace) return;

            // Check if there are any height changes between dimensionsChanges and dimensions
            const hasHeightChanges = dimensionsChanges.some((change) => {
                const currentDimensions = dimensions.current[change.id];
                return !currentDimensions || currentDimensions.height !== change.dimensions.height;
            });

            if (!hasHeightChanges && !isAReplace) return;

            dimensionsChanges.forEach((change) => {
                dimensions.current[change.id] = change.dimensions;
            });
            const newNodes = getLayoutedElements(nodes, edges, dimensions.current);
            setNodes(newNodes);
            setEdges((currentEdges) => currentEdges.map((edge) => ({ ...edge, hidden: false })));
        },
        [edges, nodes, setEdges, setNodes],
    );

    useEffect(() => {
        setTimeout(() => {
            reactFlow.fitView({
                maxZoom: 1,
            });
        }, 100);
    }, [reactFlow]);

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            proOptions={proOptions}
            defaultEdgeOptions={defaultEdgeOptions}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            nodesFocusable={false}
            edgesFocusable={false}
            elementsSelectable={true}
            maxZoom={3}
            panOnScroll={true}
            panOnScrollSpeed={1}
        >
            <Panel position="bottom-right">
                <AutomationBuilderCanvasPanel
                    enabled={data?.enabled || false}
                    onChange={onChange}
                />
            </Panel>
            <Background color={theme.colors.grey[100]} size={3} variant={BackgroundVariant.Dots} />
        </ReactFlow>
    );
});

export default AutomationBuilderCanvasFlow;
